/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { webscriptApi } from "@/AlfrescoApi";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import configService from "@/services/ConfigService.js";
import { useUserStore } from "@/stores/user";
import { useConfigStore } from "@/stores/config";

const DASHBOARD_CONFIG_FILENAME = "dashboard-config.json";
const DASHBOARD_FILENAME = "dashboard.json";
const USER_PREFERENCES_FILENAME = "user-preferences.json";

const OPTS = {
  include: [
    "aspectNames",
    "isFavorite",
    "properties",
    "allowableOperations",
    "permissions",
    "path",
    "isLocked",
  ],
  maxItems: 500,
  skipCount: 0,
};

class DashboardService {
  /**
   * Legacy method - retrieves dashboard config from webscript
   */
  getDashboardConfig(dashboardFilename) {
    const configStore = useConfigStore();
    const instanceName = configStore.INSTANCE_NAME;
    const url = `/fr/jeci/pristy/config/${instanceName}/${dashboardFilename}`;
    return webscriptApi
      .executeWebScript("GET", url)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Retrieves the user's hidden 'Configuration' folder.
   * @private
   */
  async _getHiddenFolder(userId, createIfMissing = false) {
    const homeFolderResponse =
      await alfrescoNodeService.getHomeFolderFromUser(userId);
    const homeFolderId =
      homeFolderResponse.list.entries[0].entry.properties["cm:homeFolder"];
    if (!homeFolderId) {
      throw new Error("No user home folder found");
    }

    const homeFolderChildren = await alfrescoNodeService.getChildrenWithOpts(
      homeFolderId,
      OPTS,
      false,
    );
    let hiddenFolder = homeFolderChildren.children.find(
      (entry) => entry.aspectNames && entry.aspectNames.includes("pm:hidden"),
    );

    if (!hiddenFolder && createIfMissing) {
      console.warn("No hidden folder found, creating one...");
      const opts = {
        aspectNames: ["pm:hidden"],
        nodeType: "cm:folder",
        name: "Configuration",
      };
      try {
        const createdNode = await alfrescoNodeService.createNode(
          homeFolderId,
          opts,
        );
        hiddenFolder = createdNode.entry;
      } catch (error) {
        // Handle 409 conflict (folder already exists from concurrent request)
        const is409 =
          error.status === 409 ||
          error.statusCode === 409 ||
          error.error?.statusCode === 409 ||
          error.message?.includes("Duplicate") ||
          error.message?.includes("409");
        if (is409) {
          // Force cache refresh to get the existing folder
          const retryChildren = await alfrescoNodeService.getChildrenWithOpts(
            homeFolderId,
            OPTS,
            true,
          );
          hiddenFolder = retryChildren.children.find(
            (entry) =>
              entry.aspectNames && entry.aspectNames.includes("pm:hidden"),
          );
          if (!hiddenFolder) {
            throw new Error("Failed to retrieve Configuration folder");
          }
        } else {
          throw error;
        }
      }
    }

    return hiddenFolder;
  }

  /**
   * Retrieves or creates the 'dashboard.json' file within the user's hidden configuration folder.
   * @private
   */
  async _getOrCreateDashboardFile(hiddenFolderId, createIfMissing = false) {
    const hiddenChildrenResponse =
      await alfrescoNodeService.getChildrenWithOpts(hiddenFolderId, OPTS, true);
    let dashboardFile = hiddenChildrenResponse.children.find(
      (f) => f.name === DASHBOARD_FILENAME,
    );
    if (!dashboardFile && createIfMissing) {
      console.warn(`No ${DASHBOARD_FILENAME} file found, creating one...`);
      const fileOpts = {
        name: DASHBOARD_FILENAME,
        nodeType: "cm:content",
      };
      const initialStructure = {
        key: "personal",
        label: "Personal Dashboard",
        configJson: {
          gridSettings: {
            columns: 3,
            rows: 4,
          },
          blocConfig: [],
        },
      };
      const initialContent = new Blob(
        [JSON.stringify(initialStructure, null, 2)],
        { type: "application/json" },
      );
      try {
        const createdFileNode = await alfrescoFileService.uploadFile(
          hiddenFolderId,
          fileOpts,
          initialContent,
        );

        const nodeToUpdate = createdFileNode.entry;
        const updatedNode = await alfrescoNodeService.updateNode(
          nodeToUpdate.id,
          {
            aspectNames: [...(nodeToUpdate.aspectNames || []), "pm:hidden"],
          },
        );
        dashboardFile = updatedNode.entry;
      } catch (error) {
        // Handle 409 conflict (file already exists from concurrent request)
        const is409 =
          error.status === 409 ||
          error.statusCode === 409 ||
          error.error?.statusCode === 409 ||
          error.message?.includes("Duplicate") ||
          error.message?.includes("409");
        if (is409) {
          // Force cache refresh to get the existing file
          const retryChildren = await alfrescoNodeService.getChildrenWithOpts(
            hiddenFolderId,
            OPTS,
            true,
          );
          dashboardFile = retryChildren.children.find(
            (f) => f.name === DASHBOARD_FILENAME,
          );
          if (!dashboardFile) {
            throw new Error("Failed to retrieve dashboard file");
          }
        } else {
          throw error;
        }
      }
    }
    return dashboardFile;
  }

  /**
   * Get content from Alfresco node
   * @private
   */
  async _getContent(nodeId, versionId, attachment) {
    try {
      return await alfrescoNodeService.getVersionContent(
        nodeId,
        versionId,
        attachment,
      );
    } catch (err) {
      console.error("Error getting content:", err.message);
      throw err;
    }
  }

  /**
   * Parses the JSON content of a given file node.
   * @private
   */
  async _parseFileContent(file) {
    let fileText;
    try {
      const fileContent = await this._getContent(
        file.id,
        file.properties["cm:versionLabel"],
        true,
      );
      const blobToText = (blob) =>
        new Promise((resolve, reject) => {
          if (!(blob instanceof Blob)) {
            resolve(blob);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(blob, "utf-8");
        });
      fileText = await blobToText(fileContent);
    } catch (error) {
      console.warn(
        `Could not get content for node ${file.id}, assuming empty.`,
        error.message,
      );
      fileText = "";
    }
    if (!fileText || fileText.trim() === "") {
      return null;
    }

    try {
      return JSON.parse(fileText);
    } catch (parseError) {
      console.error("Error parsing JSON content:", parseError);
      return null;
    }
  }

  /**
   * Get user preferences from Configuration folder
   * Returns default preferences if file doesn't exist
   */
  async getUserPreferences() {
    const configStore = useConfigStore();
    if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
      return { dashboard: { preferredScope: "global" } };
    }

    try {
      const user = useUserStore().person;
      if (!user) {
        return { dashboard: { preferredScope: "global" } };
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, false);
      if (!hiddenFolder) {
        return { dashboard: { preferredScope: "global" } };
      }

      const hiddenChildren = await alfrescoNodeService.getChildrenWithOpts(
        hiddenFolder.id,
        OPTS,
        true,
      );

      const prefsFile = hiddenChildren.children.find(
        (f) => f.name === USER_PREFERENCES_FILENAME,
      );

      if (!prefsFile) {
        return { dashboard: { preferredScope: "global" } };
      }

      const content = await this._parseFileContent(prefsFile);
      return content || { dashboard: { preferredScope: "global" } };
    } catch (error) {
      console.error("Error loading user preferences:", error);
      return { dashboard: { preferredScope: "global" } };
    }
  }

  /**
   * Set dashboard preference for user
   * @param {string} preferredScope - Either 'user' or 'global'
   */
  async setDashboardPreference(preferredScope) {
    const configStore = useConfigStore();
    if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
      return null;
    }

    const user = useUserStore().person;
    if (!user) {
      throw new Error("User not found");
    }

    const hiddenFolder = await this._getHiddenFolder(user.id, true);

    const hiddenChildren = await alfrescoNodeService.getChildrenWithOpts(
      hiddenFolder.id,
      OPTS,
      true,
    );

    let prefsFile = hiddenChildren.children.find(
      (f) => f.name === USER_PREFERENCES_FILENAME,
    );

    // Get existing preferences or create new
    let preferences = { dashboard: { preferredScope: "global" } };
    if (prefsFile) {
      const existing = await this._parseFileContent(prefsFile);
      preferences = existing || preferences;
    }

    // Update dashboard preference
    preferences.dashboard = preferences.dashboard || {};
    preferences.dashboard.preferredScope = preferredScope;

    const prefsContent = JSON.stringify(preferences, null, 2);
    const blob = new Blob([prefsContent], { type: "application/json" });

    if (prefsFile) {
      // Update existing file
      await alfrescoNodeService.updateContentNode(
        prefsFile.id,
        blob,
        "MINEURE",
        null,
      );
    } else {
      // Create new file
      const fileOpts = {
        name: USER_PREFERENCES_FILENAME,
        nodeType: "cm:content",
      };
      try {
        const createdFile = await alfrescoFileService.uploadFile(
          hiddenFolder.id,
          fileOpts,
          blob,
        );

        await alfrescoNodeService.updateNode(createdFile.entry.id, {
          aspectNames: [...(createdFile.entry.aspectNames || []), "pm:hidden"],
        });
      } catch (error) {
        // Handle 409 conflict (file already exists from concurrent request)
        const is409 =
          error.status === 409 ||
          error.statusCode === 409 ||
          error.error?.statusCode === 409 ||
          error.message?.includes("Duplicate") ||
          error.message?.includes("409");
        if (is409) {
          // Force cache refresh to get the existing file and update it
          const retryChildren = await alfrescoNodeService.getChildrenWithOpts(
            hiddenFolder.id,
            OPTS,
            true,
          );
          prefsFile = retryChildren.children.find(
            (f) => f.name === USER_PREFERENCES_FILENAME,
          );
          if (prefsFile) {
            await alfrescoNodeService.updateContentNode(
              prefsFile.id,
              blob,
              "MINEURE",
              null,
            );
          } else {
            throw new Error("Failed to retrieve preferences file");
          }
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Check if user has a personal dashboard
   */
  async hasPersonalDashboard() {
    const configStore = useConfigStore();
    if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
      return false;
    }

    try {
      const user = useUserStore().person;
      if (!user) {
        return false;
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, false);
      if (!hiddenFolder) {
        return false;
      }

      const dashboardFile = await this._getOrCreateDashboardFile(
        hiddenFolder.id,
        false,
      );

      return !!dashboardFile;
    } catch (error) {
      console.error("Error checking personal dashboard:", error);
      return false;
    }
  }

  /**
   * Load personal dashboard config from user's Configuration folder
   */
  async getPersonalDashboard() {
    const configStore = useConfigStore();
    if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
      return null;
    }

    try {
      const user = useUserStore().person;
      if (!user) {
        console.warn("No user found");
        return null;
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, false);
      if (!hiddenFolder) {
        return null;
      }

      const dashboardFile = await this._getOrCreateDashboardFile(
        hiddenFolder.id,
        false,
      );
      if (!dashboardFile) {
        return null;
      }

      return await this._parseFileContent(dashboardFile);
    } catch (error) {
      console.error("Error loading personal dashboard:", error.message);
      return null;
    }
  }

  /**
   * Load global dashboard config from dashboard-config.json
   */
  async getGlobalDashboard() {
    try {
      const configStore = useConfigStore();
      const instanceName = configStore.INSTANCE_NAME;
      const config = await configService.getConfig(
        instanceName,
        DASHBOARD_CONFIG_FILENAME,
      );
      return config.jsonContent || null;
    } catch (error) {
      console.error("Error loading global dashboard:", error.message);
      return null;
    }
  }

  /**
   * Load dashboard based on user preference
   * Returns an object with { config, scope }
   */
  async loadDashboard() {
    const configStore = useConfigStore();

    // If personal dashboards are disabled, always return global
    if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
      const globalDashboard = await this.getGlobalDashboard();
      if (globalDashboard) {
        return { config: globalDashboard, scope: "global" };
      }
      return null;
    }

    // Get user preference
    const prefs = await this.getUserPreferences();
    const preferredScope = prefs?.dashboard?.preferredScope || "global";

    if (preferredScope === "global") {
      // Try global first, fallback to personal
      const globalDashboard = await this.getGlobalDashboard();
      if (globalDashboard) {
        return { config: globalDashboard, scope: "global" };
      }

      const personalDashboard = await this.getPersonalDashboard();
      if (personalDashboard) {
        return { config: personalDashboard, scope: "user" };
      }
    } else {
      // Try personal first, fallback to global
      const personalDashboard = await this.getPersonalDashboard();
      if (personalDashboard) {
        return { config: personalDashboard, scope: "user" };
      }

      const globalDashboard = await this.getGlobalDashboard();
      if (globalDashboard) {
        return { config: globalDashboard, scope: "global" };
      }
    }

    return null;
  }

  /**
   * Get version history for user's dashboard file
   * Returns array of versions with metadata
   */
  async getDashboardVersionHistory(scope = "user") {
    try {
      let nodeId;

      if (scope === "global") {
        // Get global config file UUID
        const configStore = useConfigStore();
        const instanceName = configStore.INSTANCE_NAME;
        const config = await configService.getConfig(
          instanceName,
          DASHBOARD_CONFIG_FILENAME,
        );
        if (!config || !config.uuid) {
          return [];
        }
        nodeId = config.uuid;
      } else {
        // Get user's personal dashboard file
        const user = useUserStore().person;
        if (!user) {
          throw new Error("User not found");
        }

        const hiddenFolder = await this._getHiddenFolder(user.id, false);
        if (!hiddenFolder) {
          return [];
        }

        const dashboardFile = await this._getOrCreateDashboardFile(
          hiddenFolder.id,
          false,
        );
        if (!dashboardFile) {
          return [];
        }
        nodeId = dashboardFile.id;
      }

      // Get version history from Alfresco
      const versions = await alfrescoNodeService.listNodeVersionHistory(nodeId);

      if (!versions || !versions.list || !versions.list.entries) {
        return [];
      }

      // Transform and sort versions (most recent first)
      return versions.list.entries
        .map((entry) => ({
          id: entry.entry.id,
          versionLabel: entry.entry.versionComment || entry.entry.id,
          versionNumber: entry.entry.id,
          modifiedAt: entry.entry.modifiedAt,
          modifiedByUser: entry.entry.modifiedByUser,
        }))
        .sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt));
    } catch (error) {
      console.error("Error getting version history:", error);
      return [];
    }
  }

  /**
   * Revert dashboard to a specific version
   * @param {string} versionId - The version ID to revert to
   * @param {string} scope - Either 'user' or 'global'
   * @param {string} comment - Version comment (optional)
   */
  async revertDashboardToVersion(versionId, scope = "user", comment = "") {
    let nodeId;

    if (scope === "global") {
      // Get global config file UUID
      const configStore = useConfigStore();
      const instanceName = configStore.INSTANCE_NAME;
      const config = await configService.getConfig(
        instanceName,
        DASHBOARD_CONFIG_FILENAME,
      );
      if (!config || !config.uuid) {
        throw new Error("Global dashboard config not found");
      }
      nodeId = config.uuid;

      // Revert to the specified version in Alfresco
      await alfrescoNodeService.revertVersion(nodeId, versionId, comment);

      // Re-fetch the config to get updated content
      const updatedConfig = await configService.getConfig(
        instanceName,
        DASHBOARD_CONFIG_FILENAME,
      );
      return updatedConfig.jsonContent;
    } else {
      // User's personal dashboard
      const user = useUserStore().person;
      if (!user) {
        throw new Error("User not found");
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, false);
      if (!hiddenFolder) {
        throw new Error("Dashboard file not found");
      }

      const dashboardFile = await this._getOrCreateDashboardFile(
        hiddenFolder.id,
        false,
      );
      if (!dashboardFile) {
        throw new Error("Dashboard file not found");
      }
      nodeId = dashboardFile.id;

      // Revert to the specified version in Alfresco
      await alfrescoNodeService.revertVersion(nodeId, versionId, comment);

      // Re-fetch the dashboard file to get updated properties
      const hiddenChildrenResponse =
        await alfrescoNodeService.getChildrenWithOpts(
          hiddenFolder.id,
          OPTS,
          true,
        );
      const refreshedFile = hiddenChildrenResponse.children.find(
        (f) => f.name === DASHBOARD_FILENAME,
      );

      if (!refreshedFile) {
        throw new Error("Dashboard file not found after revert");
      }

      // Parse and return the reverted content
      return await this._parseFileContent(refreshedFile);
    }
  }

  /**
   * Save dashboard configuration with scope
   * @param {Object} dashboardConfig - The dashboard configuration to save
   * @param {string} scope - Either 'user' or 'global'
   */
  async saveDashboard(dashboardConfig, scope = "user") {
    // Validate that all blocks fit within grid bounds
    const gridSettings = dashboardConfig?.configJson?.gridSettings;
    const blocks = dashboardConfig?.configJson?.blocConfig || [];

    if (gridSettings && gridSettings.columns && gridSettings.rows) {
      for (const block of blocks) {
        const blockRight = (block.x || 0) + (block.w || 1);
        const blockBottom = (block.y || 0) + (block.h || 1);

        if (blockRight > gridSettings.columns) {
          throw new Error(
            `Block "${block.blockName || block.i}" extends beyond grid width (position: ${block.x}, width: ${block.w}, grid columns: ${gridSettings.columns})`,
          );
        }

        if (blockBottom > gridSettings.rows) {
          throw new Error(
            `Block "${block.blockName || block.i}" extends beyond grid height (position: ${block.y}, height: ${block.h}, grid rows: ${gridSettings.rows})`,
          );
        }
      }
    }

    if (scope === "global") {
      // Save to global config
      const configStore = useConfigStore();
      const instanceName = configStore.INSTANCE_NAME;
      await configService.updateConfig(
        instanceName,
        DASHBOARD_CONFIG_FILENAME,
        dashboardConfig,
      );
    } else if (scope === "user") {
      const configStore = useConfigStore();
      if (!configStore.DASHBOARD_PERSONAL_ENABLED) {
        throw new Error("Personal dashboards are disabled");
      }

      // Save to personal config
      const user = useUserStore().person;
      if (!user) {
        throw new Error("User not found");
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, true);
      const dashboardFile = await this._getOrCreateDashboardFile(
        hiddenFolder.id,
        true,
      );

      const updatedContent = JSON.stringify(dashboardConfig, null, 2);
      const blob = new Blob([updatedContent], { type: "application/json" });

      await alfrescoNodeService.updateContentNode(
        dashboardFile.id,
        blob,
        "MAJEURE",
        null,
      );

      // Automatically set preference to user when saving personal dashboard
      await this.setDashboardPreference("user");
    }
  }
}

export default new DashboardService();
